import { ReactElement } from 'react';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { isDarkMode } from '@localstack/services';
import { styled, useMediaQuery, useTheme } from '@mui/material';
import { blueGrey, grey } from '@mui/material/colors';
import { Link as RouterLink } from 'react-router-dom';

export interface BreadcrumbItem {
  label: string;
  href?: string;
  icon?: JSX.Element;
}

interface InstanceBreadcrumbsProps {
  items?: BreadcrumbItem[];
}

const BreadcrumbItem = styled('div')(({ theme }) => ({
  fontSize: 16,
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
}));

const StyledBreadcrumbs = styled(Breadcrumbs)(({ theme }) => ({
  color: isDarkMode(theme) ? grey[100] : blueGrey[900],
  [theme.breakpoints.down('sm')]: {
    '& .MuiBreadcrumbs-ol > *:first-of-type > *': {
      maxWidth: 120,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
  '& .MuiBreadcrumbs-ol': {
    flexWrap: 'nowrap',
  },
  '& .MuiBreadcrumbs-separator': {
    marginLeft: 0,
    marginRight: 0,
  },
}));

export const BaseHeaderBreadcrumbs = ({ items }: InstanceBreadcrumbsProps): ReactElement => {
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down('sm'));

  // Conditionally hide first breadcrumb item on small screens (if more than one item)
  const displayItems = isSm && items && items.length > 1 ? items.slice(1) : items;

  return (
    <StyledBreadcrumbs aria-label="breadcrumb" separator={<KeyboardArrowRightIcon fontSize="medium" color="inherit" />}>
      {displayItems?.map((item, index) => {
        const isLast = index === displayItems.length - 1;

        if (!isLast && item.href) {
          return (
            <BreadcrumbItem key={item.label}>
              {item.icon}
              <Link component={RouterLink} color="inherit" to={item.href} underline="none">
                {item.label}
              </Link>
            </BreadcrumbItem>
          );
        }

        return (
          <BreadcrumbItem key={item.label}>
            {item.icon}
            <span>{item.label}</span>
          </BreadcrumbItem>
        );
      })}
    </StyledBreadcrumbs>
  );
};
